import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { ChevronRight } from "lucide-react";

const getTabFromPath = (path) => {
  if (path === "/politique-confidentialite") return "privacy";
  if (path === "/cgv") return "cgv";
  return "mentions";
};

const LegalPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      
      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
            <Link 
              to="/mentions-legales"
              onClick={() => setActiveTab("mentions")}
              className={`px-4 py-2 font-bold text-sm uppercase transition-colors ${
                activeTab === "mentions" 
                  ? "bg-[#CCFF00] text-black" 
                  : "bg-[#18181B] text-white hover:bg-white/5"
              }`}
            >
              Mentions Légales
            </Link>
            <Link 
              to="/politique-confidentialite"
              onClick={() => setActiveTab("privacy")}
              className={`px-4 py-2 font-bold text-sm uppercase transition-colors ${
                activeTab === "privacy" 
                  ? "bg-[#CCFF00] text-black" 
                  : "bg-[#18181B] text-white hover:bg-white/5"
              }`}
            >
              Politique de Confidentialité
            </Link>
            <Link 
              to="/cgv"
              onClick={() => setActiveTab("cgv")}
              className={`px-4 py-2 font-bold text-sm uppercase transition-colors ${
                activeTab === "cgv" 
                  ? "bg-[#CCFF00] text-black" 
                  : "bg-[#18181B] text-white hover:bg-white/5"
              }`}
            >
              CGV
            </Link>
          </div>

          {/* Content */}
          <div className="bg-[#18181B] border border-white/10 p-6 md:p-10">
            {activeTab === "mentions" && <MentionsLegales />}
            {activeTab === "privacy" && <PolitiqueConfidentialite />}
            {activeTab === "cgv" && <CGV />}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

const MentionsLegales = () => (
  <div className="legal-content text-zinc-300">
    <h1 className="text-3xl font-black uppercase text-white mb-2">Mentions Légales</h1>
    <p className="text-sm text-zinc-500 mb-8">Dernière mise à jour : 1er mai 2025 — Conformément aux articles 6-III et 19 de la Loi n° 2004-575 du 21 juin 2004 pour la Confiance dans l'Économie Numérique (LCEN)</p>

    <Section title="1. Éditeur du site">
      <p>Le site internet accessible à l'adresse https://www.skillsview.org (ci-après « le Site ») est édité par :</p>
      <ul className="list-none mt-4 space-y-2">
        <li><strong className="text-white">Nom et prénom :</strong> Mathis El Seghetto</li>
        <li><strong className="text-white">Nom commercial :</strong> SkillsView & Conseil</li>
        <li><strong className="text-white">Forme juridique :</strong> Entreprise individuelle (EI)</li>
        <li><strong className="text-white">SIRET :</strong> 993 849 827 00015</li>
        <li><strong className="text-white">SIREN :</strong> 993 849 827</li>
        <li><strong className="text-white">Capital social :</strong> Non applicable (entreprise individuelle)</li>
        <li><strong className="text-white">Adresse e-mail :</strong> skillsview@ikmail.com</li>
        <li><strong className="text-white">Activité principale :</strong> Service SaaS d'analyse vidéo footballistique</li>
      </ul>
      <p className="mt-4">En tant qu'entreprise individuelle, Mathis El Seghetto exerce en son nom propre et est personnellement responsable des activités exercées sous le nom commercial SkillsView & Conseil.</p>
    </Section>

    <Section title="2. Directeur de la publication">
      <ul className="list-none space-y-2">
        <li><strong className="text-white">Directeur de la publication :</strong> Mathis El Seghetto</li>
        <li><strong className="text-white">Qualité :</strong> Responsable légal et propriétaire du site</li>
        <li><strong className="text-white">Contact :</strong> skillsview@ikmail.com</li>
      </ul>
    </Section>

    <Section title="3. Hébergement du site">
      <p>Le site SkillsView.org est hébergé par :</p>
      <ul className="list-none mt-4 space-y-2">
        <li><strong className="text-white">Hébergeur :</strong> O2Switch</li>
        <li><strong className="text-white">Forme juridique :</strong> SAS au capital de 100 000 €</li>
        <li><strong className="text-white">RCS :</strong> Clermont-Ferrand 510 909 807</li>
        <li><strong className="text-white">Adresse :</strong> Chemin des Pardiaux, 63000 Clermont-Ferrand, France</li>
        <li><strong className="text-white">Site web :</strong> <a href="https://www.o2switch.fr" target="_blank" rel="noopener noreferrer" className="text-[#CCFF00] hover:underline">https://www.o2switch.fr</a></li>
      </ul>
    </Section>

    <Section title="4. Propriété intellectuelle">
      <p>L'ensemble des éléments constituant le Site (textes, images, graphismes, logo, icônes, son, logiciels, base de données, etc.) est la propriété exclusive de SkillsView & Conseil, ou fait l'objet d'une autorisation d'utilisation.</p>
      <p className="mt-4">Toute reproduction, représentation, modification, publication, adaptation, totale ou partielle des éléments du Site, quel que soit le moyen ou le procédé utilisé, est interdite, sauf autorisation écrite préalable de SkillsView & Conseil à l'adresse : skillsview@ikmail.com.</p>
      <p className="mt-4">Toute exploitation non autorisée du Site ou de l'un quelconque des éléments qu'il contient sera considérée comme constitutive d'une contrefaçon et poursuivie conformément aux dispositions des articles L.335-2 et suivants du Code de la Propriété Intellectuelle.</p>
    </Section>

    <Section title="5. Données personnelles">
      <p>Le traitement des données personnelles des utilisateurs du Site est régi par la Politique de Confidentialité accessible depuis le pied de page du Site, conformément au Règlement Général sur la Protection des Données (RGPD — Règlement UE 2016/679) et à la loi française n° 78-17 du 6 janvier 1978 relative à l'informatique, aux fichiers et aux libertés, modifiée.</p>
      <p className="mt-4">Le responsable du traitement des données personnelles est : SkillsView & Conseil — skillsview@ikmail.com.</p>
    </Section>

    <Section title="6. Cookies">
      <p>Le Site utilise des cookies techniques nécessaires à son bon fonctionnement (gestion des sessions d'authentification, préférences utilisateur). Aucun cookie publicitaire ou de tracking commercial tiers n'est déposé sans le consentement préalable de l'utilisateur.</p>
      <p className="mt-4">L'utilisateur peut configurer son navigateur pour refuser les cookies. Cependant, certaines fonctionnalités du Site nécessitent l'utilisation de cookies et pourraient ne pas fonctionner correctement en leur absence.</p>
    </Section>

    <Section title="7. Limitation de responsabilité">
      <p>SkillsView & Conseil s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur le Site, et se réserve le droit d'en corriger le contenu à tout moment et sans préavis. Toutefois, il ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations disponibles sur le Site.</p>
      <p className="mt-4">SkillsView & Conseil ne saurait être tenu responsable des dommages directs ou indirects causés au matériel de l'utilisateur lors de l'accès au Site, résultant notamment de l'utilisation d'un matériel non compatible, de l'apparition d'un bug ou d'une incompatibilité.</p>
    </Section>

    <Section title="8. Droit applicable et juridiction compétente">
      <p>Le Site et les présentes Mentions Légales sont soumis au droit français. En cas de litige, et à défaut de résolution amiable, les tribunaux français seront seuls compétents, notamment le Tribunal de commerce ou le Tribunal judiciaire compétent selon la nature du litige et le lieu de domicile de l'éditeur.</p>
    </Section>

    <Section title="9. Contact">
      <p>Pour toute question relative aux présentes Mentions Légales ou au fonctionnement du Site, vous pouvez contacter l'éditeur à l'adresse électronique suivante : <a href="mailto:skillsview@ikmail.com" className="text-[#CCFF00] hover:underline">skillsview@ikmail.com</a></p>
    </Section>
  </div>
);

const PolitiqueConfidentialite = () => (
  <div className="legal-content text-zinc-300">
    <h1 className="text-3xl font-black uppercase text-white mb-2">Politique de Confidentialité</h1>
    <p className="text-sm text-zinc-500 mb-8">Dernière mise à jour : 1er mai 2025 — Conformément au Règlement (UE) 2016/679 du Parlement européen et du Conseil du 27 avril 2016 (RGPD) et à la Loi Informatique et Libertés n° 78-17 du 6 janvier 1978 modifiée.</p>

    <Section title="1. Identité du responsable du traitement">
      <ul className="list-none space-y-2">
        <li><strong className="text-white">Responsable du traitement :</strong> Mathis El Seghetto</li>
        <li><strong className="text-white">Nom commercial :</strong> SkillsView & Conseil</li>
        <li><strong className="text-white">SIRET :</strong> 993 849 827 00015</li>
        <li><strong className="text-white">Adresse e-mail :</strong> skillsview@ikmail.com</li>
        <li><strong className="text-white">Site concerné :</strong> https://www.skillsview.org</li>
      </ul>
      <p className="mt-4">En tant que responsable du traitement, Mathis El Seghetto détermine les finalités et les moyens des traitements de données personnelles réalisés dans le cadre de l'exploitation du Site et du service d'analyse vidéo footballistique SkillsView.</p>
    </Section>

    <Section title="2. Données collectées">
      <h4 className="text-white font-bold mt-4 mb-2">2.1 Données fournies lors de l'inscription</h4>
      <ul className="list-disc list-inside space-y-1 ml-4">
        <li>Prénom et nom</li>
        <li>Adresse e-mail (utilisée notamment pour la livraison des analyses)</li>
        <li>Mot de passe (stocké sous forme hachée et salée via bcrypt — jamais en clair)</li>
        <li>Type de compte : Joueur individuel ou Club</li>
        <li>Numéro de téléphone WhatsApp (collecté lors du formulaire de commande)</li>
        <li>Acceptation des CGU et de la politique de confidentialité (horodatage conservé)</li>
      </ul>

      <h4 className="text-white font-bold mt-6 mb-2">2.2 Données collectées automatiquement</h4>
      <ul className="list-disc list-inside space-y-1 ml-4">
        <li>Adresse IP de connexion</li>
        <li>Données de navigation (pages visitées, horodatage des connexions)</li>
        <li>Type et version de navigateur, système d'exploitation</li>
        <li>Identifiant de session (cookie technique)</li>
      </ul>

      <h4 className="text-white font-bold mt-6 mb-2">2.3 Données de commande et de paiement</h4>
      <ul className="list-disc list-inside space-y-1 ml-4">
        <li>Type d'analyse commandée (collectif / individuel / abonnement)</li>
        <li>Date et heure de la commande</li>
        <li>Statut de la commande (en attente, en cours, livré)</li>
        <li>Lien SwissTransfer fourni par le client pour le dépôt de la vidéo</li>
        <li>Description de la demande d'analyse (texte libre)</li>
        <li>Référence de transaction et statut du paiement (fournis par Stripe)</li>
      </ul>

      <h4 className="text-white font-bold mt-6 mb-2">2.4 Données non collectées</h4>
      <p>SkillsView <strong className="text-white">ne stocke PAS</strong> les vidéos des clients. L'hébergement et le transfert des vidéos sont assurés exclusivement via SwissTransfer (serveurs en Suisse). Les coordonnées bancaires et données de carte de paiement ne sont jamais stockées sur les serveurs de SkillsView.</p>
    </Section>

    <Section title="3. Finalités et bases légales des traitements">
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm border border-white/10">
          <thead>
            <tr className="bg-white/5">
              <th className="text-left p-3 border-b border-white/10 text-white">Finalité</th>
              <th className="text-left p-3 border-b border-white/10 text-white">Base légale</th>
              <th className="text-left p-3 border-b border-white/10 text-white">Durée</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-white/5">
              <td className="p-3">Création et gestion du compte</td>
              <td className="p-3">Exécution du contrat</td>
              <td className="p-3">Durée du compte + 3 ans</td>
            </tr>
            <tr className="border-b border-white/5">
              <td className="p-3">Traitement des commandes</td>
              <td className="p-3">Exécution du contrat</td>
              <td className="p-3">5 ans</td>
            </tr>
            <tr className="border-b border-white/5">
              <td className="p-3">Traitement des paiements</td>
              <td className="p-3">Exécution du contrat</td>
              <td className="p-3">10 ans (obligation comptable)</td>
            </tr>
            <tr className="border-b border-white/5">
              <td className="p-3">E-mails transactionnels</td>
              <td className="p-3">Exécution du contrat</td>
              <td className="p-3">3 ans</td>
            </tr>
            <tr className="border-b border-white/5">
              <td className="p-3">Gestion des abonnements</td>
              <td className="p-3">Exécution du contrat</td>
              <td className="p-3">5 ans après résiliation</td>
            </tr>
            <tr>
              <td className="p-3">Sécurité du Site</td>
              <td className="p-3">Intérêt légitime</td>
              <td className="p-3">6 mois max</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    <Section title="4. Destinataires des données">
      <h4 className="text-white font-bold mt-4 mb-2">4.1 Usage interne</h4>
      <p>Les données personnelles sont accessibles exclusivement à SkillsView & Conseil, en qualité d'analyste et d'administrateur du Site.</p>

      <h4 className="text-white font-bold mt-6 mb-2">4.2 Sous-traitants et prestataires tiers</h4>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm border border-white/10">
          <thead>
            <tr className="bg-white/5">
              <th className="text-left p-3 border-b border-white/10 text-white">Prestataire</th>
              <th className="text-left p-3 border-b border-white/10 text-white">Rôle</th>
              <th className="text-left p-3 border-b border-white/10 text-white">Localisation</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-white/5">
              <td className="p-3">O2Switch</td>
              <td className="p-3">Hébergement</td>
              <td className="p-3">France</td>
            </tr>
            <tr className="border-b border-white/5">
              <td className="p-3">Stripe Inc.</td>
              <td className="p-3">Paiements</td>
              <td className="p-3">UE / USA</td>
            </tr>
            <tr className="border-b border-white/5">
              <td className="p-3">Resend</td>
              <td className="p-3">E-mails transactionnels</td>
              <td className="p-3">UE / USA</td>
            </tr>
            <tr>
              <td className="p-3">SwissTransfer</td>
              <td className="p-3">Transfert fichiers vidéo</td>
              <td className="p-3">Suisse</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    <Section title="5. Vos droits">
      <p>Conformément au RGPD, vous disposez des droits suivants :</p>
      <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
        <li><strong className="text-white">Droit d'accès (art. 15)</strong> — Obtenir une copie de vos données</li>
        <li><strong className="text-white">Droit de rectification (art. 16)</strong> — Corriger vos données inexactes</li>
        <li><strong className="text-white">Droit à l'effacement (art. 17)</strong> — Demander la suppression de vos données</li>
        <li><strong className="text-white">Droit à la limitation (art. 18)</strong> — Limiter le traitement de vos données</li>
        <li><strong className="text-white">Droit à la portabilité (art. 20)</strong> — Recevoir vos données dans un format lisible</li>
        <li><strong className="text-white">Droit d'opposition (art. 21)</strong> — Vous opposer au traitement</li>
      </ul>
      <p className="mt-4">Pour exercer vos droits : <a href="mailto:skillsview@ikmail.com" className="text-[#CCFF00] hover:underline">skillsview@ikmail.com</a></p>
      <p className="mt-2">Réclamation CNIL : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#CCFF00] hover:underline">www.cnil.fr</a></p>
    </Section>

    <Section title="6. Sécurité des données">
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Chiffrement des mots de passe via bcrypt</li>
        <li>Transmission chiffrée via HTTPS/TLS</li>
        <li>Sessions sécurisées par tokens JWT</li>
        <li>Protection CSRF</li>
        <li>Rate limiting sur l'authentification</li>
        <li>Données de paiement non stockées (traitées par Stripe)</li>
      </ul>
    </Section>

    <Section title="7. Contact">
      <p>Pour toute question : <a href="mailto:skillsview@ikmail.com" className="text-[#CCFF00] hover:underline">skillsview@ikmail.com</a></p>
    </Section>
  </div>
);

const CGV = () => (
  <div className="legal-content text-zinc-300">
    <h1 className="text-3xl font-black uppercase text-white mb-2">Conditions Générales de Vente</h1>
    <p className="text-sm text-zinc-500 mb-8">Version 1.0 — En vigueur à compter du 1er mai 2025 — Conformément aux articles L. 111-1 et suivants du Code de la Consommation</p>

    <Section title="Préambule">
      <p>Les présentes Conditions Générales de Vente (ci-après « CGV ») régissent l'ensemble des transactions commerciales conclues entre :</p>
      <ul className="list-disc list-inside space-y-1 ml-4 mt-4">
        <li><strong className="text-white">Le prestataire :</strong> SkillsView & Conseil, entreprise individuelle, SIRET 993 849 827, joignable à skillsview@ikmail.com</li>
        <li><strong className="text-white">Et le Client :</strong> toute personne procédant à un achat via le Site https://www.skillsview.org</li>
      </ul>
      <p className="mt-4">Toute commande passée sur le Site implique l'acceptation pleine et entière des présentes CGV.</p>
    </Section>

    <Section title="Article 1 — Description des services">
      <p>SkillsView & Conseil propose un service de commande et de livraison d'analyses vidéo tactiques et techniques de matchs de football, sous forme de plateforme SaaS.</p>
      
      <h4 className="text-white font-bold mt-6 mb-2">1.1 Nature du service</h4>
      <p>Le service consiste en une analyse approfondie, réalisée par SkillsView & Conseil (l'Analyste), à partir d'une vidéo de match fournie par le Client via SwissTransfer. L'analyse est restituée par e-mail dans le délai contractuel de 4 jours.</p>

      <h4 className="text-white font-bold mt-6 mb-2">1.2 Formules disponibles</h4>
      <div className="overflow-x-auto mt-4">
        <table className="w-full text-sm border border-white/10">
          <thead>
            <tr className="bg-white/5">
              <th className="text-left p-3 border-b border-white/10 text-white">Formule</th>
              <th className="text-left p-3 border-b border-white/10 text-white">Prix TTC</th>
              <th className="text-left p-3 border-b border-white/10 text-white">Inclus</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-white/5">
              <td className="p-3">Match à l'unité — Collectif</td>
              <td className="p-3">100,00 €</td>
              <td className="p-3">1 analyse tactique collective</td>
            </tr>
            <tr className="border-b border-white/5">
              <td className="p-3">Match à l'unité — Individuel</td>
              <td className="p-3">100,00 €</td>
              <td className="p-3">1 analyse individuelle</td>
            </tr>
            <tr>
              <td className="p-3">Abonnement mensuel</td>
              <td className="p-3">300,00 €/mois</td>
              <td className="p-3">4 analyses par mois</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Section>

    <Section title="Article 2 — Conditions d'accès au service">
      <h4 className="text-white font-bold mt-4 mb-2">2.1 Inscription obligatoire</h4>
      <p>L'accès au service nécessite la création d'un compte personnel sur le Site.</p>

      <h4 className="text-white font-bold mt-6 mb-2">2.2 Conditions d'éligibilité</h4>
      <p>Le service est accessible aux personnes physiques majeures (18 ans ou plus) ou aux personnes morales dûment représentées.</p>

      <h4 className="text-white font-bold mt-6 mb-2">2.3 Confidentialité du compte</h4>
      <p>Le Client est seul responsable de la confidentialité de ses identifiants de connexion.</p>
    </Section>

    <Section title="Article 3 — Processus de commande">
      <h4 className="text-white font-bold mt-4 mb-2">3.1 Étapes de la commande</h4>
      <ol className="list-decimal list-inside space-y-2 ml-4">
        <li>Sélection de la formule souhaitée</li>
        <li>Paiement en ligne validé via Stripe</li>
        <li>Remplissage du formulaire de commande :
          <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
            <li>Lien SwissTransfer de la vidéo</li>
            <li>Description précise de la demande (min. 50 caractères)</li>
            <li>Numéro WhatsApp</li>
          </ul>
        </li>
      </ol>

      <h4 className="text-white font-bold mt-6 mb-2">3.2 Conditions relatives à la vidéo</h4>
      <p>Le Client doit s'assurer que la vidéo est lisible, de qualité suffisante, et que le lien SwissTransfer est valide (30 jours).</p>
    </Section>

    <Section title="Article 4 — Délai de livraison">
      <div className="bg-[#CCFF00]/10 border border-[#CCFF00]/30 p-4 mt-4">
        <p className="text-white font-bold">ENGAGEMENT CONTRACTUEL : SkillsView & Conseil s'engage à livrer toute analyse dans un délai maximum de 4 jours calendaires à compter de la réception du formulaire complet.</p>
      </div>
      <p className="mt-4">En cas de force majeure, le délai pourra être prolongé avec information préalable du Client.</p>
    </Section>

    <Section title="Article 5 — Prix et paiement">
      <h4 className="text-white font-bold mt-4 mb-2">5.1 Prix</h4>
      <p>Les prix sont indiqués en euros TTC sur la page tarifaire.</p>

      <h4 className="text-white font-bold mt-6 mb-2">5.2 Modalités de paiement</h4>
      <p>Moyens acceptés : Carte bancaire (Visa, Mastercard, American Express), Virement SEPA. Le paiement est sécurisé par Stripe.</p>

      <h4 className="text-white font-bold mt-6 mb-2">5.3 Abonnement mensuel</h4>
      <p>L'abonnement est renouvelé automatiquement chaque mois. Les crédits non utilisés ne sont pas reportés. Résiliation possible à tout moment depuis l'espace compte.</p>
    </Section>

    <Section title="Article 6 — Droit de rétractation">
      <h4 className="text-white font-bold mt-4 mb-2">6.1 Principe</h4>
      <p>Le Client consommateur dispose d'un délai de 14 jours pour exercer son droit de rétractation.</p>

      <h4 className="text-white font-bold mt-6 mb-2">6.3 Exception</h4>
      <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mt-4">
        <p className="text-yellow-400 font-bold">IMPORTANT : Le droit de rétractation ne peut être exercé une fois l'analyse livrée. En commandant, le Client accepte que SkillsView & Conseil commence l'exécution immédiatement.</p>
      </div>
    </Section>

    <Section title="Article 7 — Remboursements et réclamations">
      <h4 className="text-white font-bold mt-4 mb-2">7.1 Non-respect du délai</h4>
      <p>En cas de non-livraison dans les 4 jours, le Client peut demander le remboursement intégral.</p>

      <h4 className="text-white font-bold mt-6 mb-2">7.2 Réclamations qualité</h4>
      <p>Délai de 7 jours après livraison pour formuler une réclamation motivée à skillsview@ikmail.com.</p>
    </Section>

    <Section title="Article 8 — Responsabilités">
      <p>SkillsView & Conseil ne saurait être tenu responsable :</p>
      <ul className="list-disc list-inside space-y-1 ml-4 mt-4">
        <li>Des résultats sportifs du Client</li>
        <li>De l'impossibilité d'exécuter en raison d'une vidéo illisible</li>
        <li>De tout dommage indirect</li>
      </ul>
      <p className="mt-4">La responsabilité de SkillsView & Conseil est limitée au montant de la commande concernée.</p>
    </Section>

    <Section title="Article 9 — Propriété intellectuelle">
      <p>Les analyses sont des œuvres protégées par le droit d'auteur. Le Client bénéficie d'une licence d'utilisation personnelle et non exclusive.</p>
      <ul className="list-disc list-inside space-y-1 ml-4 mt-4">
        <li><strong className="text-white">Usage autorisé :</strong> formation, progression sportive</li>
        <li><strong className="text-white">Usage interdit :</strong> revente, diffusion publique sans accord</li>
      </ul>
    </Section>

    <Section title="Article 12 — Médiation et règlement des litiges">
      <p>En cas de litige, adresser une réclamation à skillsview@ikmail.com (réponse sous 15 jours ouvrés).</p>
      <p className="mt-4">Plateforme RLL : <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-[#CCFF00] hover:underline">https://ec.europa.eu/consumers/odr</a></p>
    </Section>

    <Section title="Article 13 — Droit applicable">
      <p>Les présentes CGV sont soumises au droit français.</p>
    </Section>

    <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-zinc-500">
      <p>SkillsView & Conseil — Mathis El Seghetto — SIRET 993 849 827</p>
      <p>skillsview@ikmail.com — https://www.skillsview.org</p>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
    {children}
  </div>
);

export default LegalPage;
